/**
 * 워크스페이스 관리(콘솔) — 회사 전체 용량만 (2026-08-25 신규).
 *
 * ⚠ 화면 이름은 그냥 **워크스페이스 관리**다(사용자 지시로 '(시스템용)' 을 뗐다).
 *   8443 에도 같은 이름이 있지만 하는 일이 다르다 — 아래 설명 참조.
 *
 * ── 왜 이만큼인가 (사용자 결정) ────────────────────────────────────────────
 * 워크스페이스 관리는 **둘로 나뉜다**:
 *   · **회사용** — 그 회사에 할당된 용량을 부서·팀·개인에게 나눈다. 자리는 8443
 *     통합설정 › 회사 관리 › 워크스페이스 관리 이고, 회사 관리자가 쓴다.
 *   · **시스템용(여기)** — 회사에 **얼마를 주느냐**만 정한다. 나누는 일은 회사가 한다.
 *
 * 그래서 이 화면은 조직 트리도 사람 목록도 두지 않는다. 회사 전체 사용량과 한도 하나뿐이다.
 * 이전 화면 `WorkspaceQuotaWorkspace.tsx`(좌 조직 트리 / 우 사람)는 **무수정으로 남겨 뒀다** —
 * 롤백 대비이고, 그 화면은 8443 으로 옮겨 가 회사용으로 계속 산다.
 *
 * 서버 SSOT — 전부 이미 있는 것들이다(신규 엔드포인트 0건)
 *  - GET  /admin/workspace/overview   회사 합계 · 한도 · 종류별
 *  - GET  /admin/workspace/defaults   기본 한도(안내용)
 *  - POST /admin/workspace/quota      scopeType='company' 한도 저장
 *
 * ⚠ 루트에 `um-ws` 를 반드시 함께 붙인다 — `--um-mono`·`--um-tint-*` 가 그 스코프에만 있다.
 */
import { useCallback, useEffect, useState } from 'react';
import './UserWorkspace.css';
import './WorkspaceQuota.css';
import { AlertTriangle, Check, Edit2, X } from '../../../icons';
import {
  getWorkspaceOverview, setWorkspaceQuota, getQuotaDefaults,
  formatBytes, bytesToGb, gbToBytes,
  type WorkspaceOverview, type QuotaDefaults,
} from '../../../api/workspaceAdmin';
import { getInstitution } from '../../../api/institution';
import { ConsoleScreenHead } from '../../../components/console-list/ConsoleScreenHead';

/* ─────────────────────────── 상수 · 유틸 ─────────────────────────── */

/** 서버 FILE_KINDS 9종. 없는 키는 키를 그대로 보여 준다. */
const KIND_LABEL: Record<string, string> = {
  word: 'Word', excel: 'Excel', ppt: 'PPT', hwp: '한글',
  pdf: 'PDF', markup: '마크업', txt: 'txt', image: '이미지', etc: '그외',
};

type Tone = 'ok' | 'warn' | 'over' | 'unlimited';
const TONE_LABEL: Record<Tone, string> = { ok: '여유', warn: '경고', over: '초과', unlimited: '무제한' };

function toneOf(pct: number, unlimited: boolean): Tone {
  if (unlimited) return 'unlimited';
  if (pct >= 100) return 'over';
  if (pct >= 80) return 'warn';
  return 'ok';
}

function errMsg(e: unknown, fallback: string): string {
  const r = e as { response?: { data?: { message?: string; error?: string; violations?: string[] } }; message?: string };
  const d = r?.response?.data;
  const v = Array.isArray(d?.violations) && d.violations.length ? ` (${d.violations.join(', ')})` : '';
  return `${d?.message ?? d?.error ?? r?.message ?? fallback}${v}`;
}

/** 카드가 높아지지 않게 큰 것 몇 개만 보이고 나머지는 하나로 접는다. */
const KIND_CHIPS_MAX = 6;
function KindChips({ byKind }: { byKind: WorkspaceOverview['byKind'] }) {
  const kinds = byKind.filter((k) => k.count > 0).slice().sort((a, b) => b.bytes - a.bytes);
  if (kinds.length === 0) return null;
  const head = kinds.slice(0, KIND_CHIPS_MAX);
  const rest = kinds.slice(KIND_CHIPS_MAX);
  const restBytes = rest.reduce((s, k) => s + k.bytes, 0);
  return (
    <div className="wsq-kinds" aria-label="파일 종류별 용량">
      {head.map((k) => (
        <span key={k.kind} className="wsq-kind" title={`${k.count.toLocaleString()}개`}>
          <b>{KIND_LABEL[k.kind] ?? k.kind}</b>
          <span className="mono">{formatBytes(k.bytes)}</span>
        </span>
      ))}
      {rest.length > 0 && (
        <span className="wsq-kind" title={rest.map((k) => `${KIND_LABEL[k.kind] ?? k.kind} ${formatBytes(k.bytes)}`).join(' · ')}>
          <b>그 밖 {rest.length}종</b>
          <span className="mono">{formatBytes(restBytes)}</span>
        </span>
      )}
    </div>
  );
}

/* ═══════════════════════════ 본체 ═══════════════════════════ */

export function WorkspaceCompanySection() {
  const [overview, setOverview] = useState<WorkspaceOverview | null>(null);
  const [defaults, setDefaults] = useState<QuotaDefaults | null>(null);
  const [company, setCompany] = useState('');
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      // 회사명은 실패해도 화면을 막지 않는다 — 제목만 폴백된다.
      const [ov, df, inst] = await Promise.all([
        getWorkspaceOverview(),
        getQuotaDefaults().catch(() => null),
        getInstitution().catch(() => null),
      ]);
      setOverview(ov);
      setDefaults(df);
      setCompany(inst?.name ?? '');
    } catch (e) {
      setErr(errMsg(e, '용량 정보를 불러오지 못했습니다.'));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { void reload(); }, [reload]);

  useEffect(() => {
    if (!notice) return;
    const t = setTimeout(() => setNotice(null), 4000);
    return () => clearTimeout(t);
  }, [notice]);

  const unlimited = overview?.company.unlimited ?? true;
  const pct = overview?.company.pct ?? 0;
  const tone = toneOf(pct, unlimited);

  return (
    <section className="svc-cat svc-cat--wsq um-ws um-ws--flat">
      <ConsoleScreenHead
        title="워크스페이스 관리"
        desc="회사에 줄 전체 용량만 정합니다 — 부서·팀·개인 배분은 회사가 합니다."
      />

      {err && <div className="admin-console__alert admin-console__alert--warn" role="alert">{err}</div>}
      {notice && <div className="um-alert um-alert--ok" role="status"><Check size={15} /><span>{notice}</span></div>}

      {loading ? (
        <p className="svc-empty">불러오는 중…</p>
      ) : !overview ? (
        <p className="svc-empty">용량 정보가 없습니다.</p>
      ) : (
        <div className="wsc-card">
          <div className="wsc-card__top">
            <span className="um-mk um-mk--company" aria-hidden><i /></span>
            <span className="wsc-card__name">
              <b>{company || '회사 전체'}</b>
              <span>모든 사용자·팀 합계</span>
            </span>
            <span className={`um-st um-st--${tone}`}>{TONE_LABEL[tone]}</span>
            <button type="button" className="um-btn um-btn--primary um-btn--sm" onClick={() => setEditing(true)}>
              <Edit2 size={13} /> 회사 한도 수정
            </button>
          </div>

          <div className="wsq-use wsq-use--lg">
            <span className="wsq-use__n">
              {formatBytes(overview.totalBytes)}
              <span className="sep"> / </span>
              <span className="lim">{unlimited ? '무제한' : formatBytes(overview.company.limitBytes)}</span>
            </span>
            {!unlimited && (
              <span className={`wsq-bar wsq-bar--${tone}`} aria-hidden>
                <i style={{ width: `${Math.min(100, pct)}%` }} />
              </span>
            )}
            <span className="um-f__hint">
              파일 {overview.fileCount.toLocaleString()}개{unlimited ? '' : ` · 한도의 ${pct}%`}
            </span>
          </div>

          <KindChips byKind={overview.byKind} />

          <p className="wsc-note">
            여기서 정한 값이 이 회사가 쓸 수 있는 <b>전체 용량</b>입니다.
            그 안에서 부서·팀·개인에게 얼마씩 나눌지는 회사 관리자가
            <b> 통합설정 › 회사 관리 › 워크스페이스 관리</b> 에서 정합니다.
          </p>
        </div>
      )}

      {editing && overview && (
        <CompanyQuotaDrawer
          name={company || '회사 전체'}
          usedBytes={overview.totalBytes}
          limitBytes={overview.company.limitBytes}
          unlimited={unlimited}
          pct={pct}
          fileCount={overview.fileCount}
          defaultLimit={defaults?.company ?? null}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            setNotice('회사 전체 용량 한도를 저장했습니다.');
            void reload();
          }}
        />
      )}
    </section>
  );
}

/* ─────────────────────── 한도 수정 드로어 ─────────────────────── */

function CompanyQuotaDrawer({
  name, usedBytes, limitBytes, unlimited: initUnlimited, pct, fileCount, defaultLimit, onClose, onSaved,
}: {
  name: string; usedBytes: number; limitBytes: number; unlimited: boolean;
  pct: number; fileCount: number; defaultLimit: number | null;
  onClose: () => void; onSaved: () => void;
}) {
  const [unlimited, setUnlimited] = useState(initUnlimited);
  const [gb, setGb] = useState(() => (initUnlimited
    ? bytesToGb(defaultLimit != null && defaultLimit > 0 ? defaultLimit : usedBytes)
    : bytesToGb(limitBytes)));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [onClose]);

  const tone = toneOf(pct, initUnlimited);
  const belowUsage = !unlimited && gbToBytes(gb) < usedBytes;

  async function save() {
    setSaving(true); setErr(null);
    try {
      // 회사 범위는 scopeId 를 보내지 않는다 — 서버가 JWT 의 institution 으로 스코프한다.
      await setWorkspaceQuota({ scopeType: 'company', limitBytes: unlimited ? -1 : gbToBytes(gb) });
      onSaved();
    } catch (e) {
      setErr(errMsg(e, '저장에 실패했습니다.'));
      setSaving(false);
    }
  }

  return (
    <>
      <div className="um-scrim" role="presentation" onClick={onClose} />
      <aside className="um-dr um-ws" role="dialog" aria-modal="true" aria-label="회사 전체 용량 한도 수정">
        <div className="um-dr__h">
          <span className="um-mk um-mk--company" aria-hidden><i /></span>
          <span className="um-dr__t">
            <b>{name}</b>
            <span>회사 · 모든 사용자·팀 합계</span>
          </span>
          <span className={`um-st um-st--${tone}`}>{TONE_LABEL[tone]}</span>
          <button type="button" className="um-dr__x" aria-label="닫기" onClick={onClose}><X size={15} /></button>
        </div>

        <div className="um-dr__b">
          {err && <div className="um-alert" role="alert" style={{ margin: '0 0 14px' }}><AlertTriangle size={15} /><span>{err}</span></div>}

          <div className="um-secl">현재 사용량</div>
          <div className="wsq-use wsq-use--lg">
            <span className="wsq-use__n">
              {formatBytes(usedBytes)}
              <span className="sep"> / </span>
              <span className="lim">{initUnlimited ? '무제한' : formatBytes(limitBytes)}</span>
            </span>
            {!initUnlimited && (
              <span className={`wsq-bar wsq-bar--${tone}`} aria-hidden>
                <i style={{ width: `${Math.min(100, pct)}%` }} />
              </span>
            )}
            <span className="um-f__hint">
              파일 {fileCount.toLocaleString()}개{initUnlimited ? '' : ` · 한도의 ${pct}%`}
            </span>
          </div>

          <div className="um-secl">한도</div>
          <div className="um-fields">
            <div className="um-f w2">
              <label className="um-chk">
                <input type="checkbox" checked={unlimited} onChange={(e) => setUnlimited(e.target.checked)} />
                무제한
              </label>
              <span className="um-f__hint">한도 없이 업로드를 허용합니다. 끄면 아래에서 GB 한도를 지정합니다.</span>
            </div>

            {!unlimited && (
              <div className="um-f w2">
                <label htmlFor="wsc-gb">한도 (GB)</label>
                <input id="wsc-gb" className="um-inp" type="number" min={0} step={1} value={gb}
                  onChange={(e) => setGb(Math.max(0, Number(e.target.value) || 0))} />
                <span className="um-f__hint">
                  {defaultLimit != null && defaultLimit >= 0 && `기본값 ${formatBytes(defaultLimit)}. `}
                  한도를 넘으면 새 업로드가 차단됩니다(기존 파일은 그대로 유지).
                </span>
              </div>
            )}
          </div>

          {belowUsage && (
            <div className="um-alert um-alert--warn" role="alert">
              <AlertTriangle size={15} />
              <span>현재 사용량({formatBytes(usedBytes)})보다 작은 한도입니다 — 저장 즉시 이 회사의 업로드가 전부 막힙니다.</span>
            </div>
          )}
        </div>

        <div className="um-dr__f">
          <button type="button" className="um-btn um-btn--primary" disabled={saving} onClick={() => void save()}>
            {saving ? '저장 중…' : '저장'}
          </button>
          <button type="button" className="um-btn" onClick={onClose}>닫기</button>
          <span className="sp" />
        </div>
      </aside>
    </>
  );
}
